
import React, { useState, useEffect } from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { muiOptions, MuiProps } from '../../../infrastructure/materialUiThemeProvider'
import { t } from '../../../infrastructure/i18nextHelper'
import { api } from '../../../infrastructure/api'
import guid,{ Guid } from '../../../infrastructure/guid'
import { ExcelGeneratorContainer } from '../../../infrastructure/excelExport'
import { ColumnDescriptor, TableItem, TextField, NumberField, Select } from '../../common/customComponents'
import { MasterDataItem, MasterDataShell, createExcelLines } from './masterDataShell'
import { applyFilters } from '../../common/filtersHelper'
import { SnackbarContainer } from '../../../infrastructure/snackbars'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { Claims } from '../../../infrastructure/signIn/models'
import { Company, Product, Site, CustomerSegment} from './models'

type ChannelCustomerSegmentProduct = {
    id: Guid
    company: string
    customerSegment: string
    product: string
    site: string
    mondayWeight: number
    tuesdayWeight: number
    wednesdayWeight: number
    thursdayWeight: number
    fridayWeight: number
    saturdayWeight: number
    sundayWeight: number
}

type ChannelCustomerSegmentProductFilters = {
    company: string | null
    customerSegment: string | null
    product: string | null
    site: string | null
}

let noFilters: ChannelCustomerSegmentProductFilters = {
    company: null,
    customerSegment: null,
    product: null,
    site: null
}

function CustomerSegmentProductMasterData({ classes }: MuiProps) {
    let excelGenerator = ExcelGeneratorContainer.useContainer()
    let [items, setItems] = useState<TableItem<MasterDataItem<ChannelCustomerSegmentProduct>>[]>([])
    let [selectedSegments, setSelectedSegments] = useState<TableItem<MasterDataItem<ChannelCustomerSegmentProduct>>[]>([])
    let [companys, setCompanys] = useState<Company[]>([])
    let [sites, setSites] = useState<Site[]>([])
    let [products, setProducts] = useState<Product[]>([])
    let [customerSegments, setCustomerSegments] = useState<CustomerSegment[]>([])
    let [filters, setFilters] = useState<ChannelCustomerSegmentProductFilters>(noFilters)
    let snackbar = SnackbarContainer.useContainer()

    const tBase = 'admin.masterdata.channelCustomerSegmentProduct.'

    const channelCustomerSegments = (company: string) => customerSegments
        .filter(x => x.company === company)
        .map(x => x.customerSegment)

    let init = async () => {
        let companyPromise = api.get<Company[]>('masterdata/productCompany/company')
        let productPromise = api.get<Product[]>('masterdata/productCompany/product')
        let sitesPromise = api.get<Site[]>('masterdata/stockCapacity/site')
        let customerSegmentsPromise = api.get<CustomerSegment[]>('masterdata/customerSegment/channelCustomerSegment')
        setCompanys(await companyPromise)
        setProducts(await productPromise)
        setSites(await sitesPromise)
        setCustomerSegments(await customerSegmentsPromise)
        await load()
    }

    let load = async () => {
        let data = await api.get<ChannelCustomerSegmentProduct[]>('masterdata/customerSegment/channelCustomerSegmentProduct')
        setItems(data.map(toTableItem))
    }

    useEffect(() => {
        init()
    }, [])

    let getItems = () => applyFilters(items, filters)

    let onSave = async (item: ChannelCustomerSegmentProduct & IsNew) => {
        await api.post('masterdata/customerSegment/channelCustomerSegmentProduct', item)
        await load()
        return true
    }

    let onDelete = async (ids: string[] | null) => {
        if (ids == null) return false
        await api.del('masterdata/customerSegment/channelCustomerSegmentProduct', ids)
        await load()
        return true
    }

    let exportExcel = () => {
        excelGenerator.generate({
            filename: 'CustomerSegmentProducts.xlsx',
            sheets: [{ name: 'CustomerSegmentProducts', lines: createExcelLines(selectedSegments, columns) }]
        })
    }

    let importExcel = async (file: Blob) => {
        try {
            await api.upload('masterdata/customerSegment/import/channelCustomerSegmentProducts', file, 'file')
            await load()
            snackbar.success(t('httpSuccess.importSuccessful'))
        } catch (error) {
            console.error('Import failed:', error)
        }
    }

    let columns: ColumnDescriptor<TableItem<MasterDataItem<ChannelCustomerSegmentProduct>>>[] = [
        {
            name: t(tBase + 'company'),
            value: x => x.company,
            columnFilter: { value: filters.company ?? '', onChange: (company: string | null) => setFilters({ ...filters, company }) }
        },
        {
            name: t(tBase + 'customerSegment'),
            value: x => x.customerSegment,
            columnFilter: { value: filters.customerSegment ?? '', onChange: (customerSegment: string | null) => setFilters({ ...filters, customerSegment }) }
        },
        {
            name: t(tBase + 'product'),
            value: x => x.product,
            columnFilter: { value: filters.product ?? '', onChange: (product: string | null) => setFilters({ ...filters, product }) }
        },
        {
            name: t(tBase + 'site'),
            value: x => x.site,
            columnFilter: { value: filters.site ?? '', onChange: (site: string | null) => setFilters({ ...filters, site }) }
        },
        {
            name: t(tBase + 'mondayWeight'),
            value: x => x.mondayWeight
        },
        {
            name: t(tBase + 'tuesdayWeight'),
            value: x => x.tuesdayWeight
        },
        {
            name: t(tBase + 'wednesdayWeight'),
            value: x => x.wednesdayWeight
        },
        {
            name: t(tBase + 'thursdayWeight'),
            value: x => x.thursdayWeight
        },
        {
            name: t(tBase + 'fridayWeight'),
            value: x => x.fridayWeight
        },
        {
            name: t(tBase + 'saturdayWeight'),
            value: x => x.saturdayWeight
        },
        {
            name: t(tBase + 'sundayWeight'),
            value: x => x.sundayWeight
        }
    ]

    let isManager = hasClaim(Claims.MasterdataCustomerSegmentProductManager)

    return (
        <div className={classes.container}>
            <MasterDataShell
                tableId={'customerSegmentProduct-table'}
                headerLabel={t(tBase + 'customerSegmentProducts')}
                itemLabel={t(tBase + 'customerSegmentProduct')}
                isManager={isManager}
                onExportExcel={exportExcel}
                onImportExcel={importExcel}
                onNew={() => emptyItem()}
                onSave={onSave}
                onDelete={onDelete}
                items={getItems()}
                onSelectionChange={setSelectedSegments}
                columns={columns}>{
                    (selectedItem, setSelectedItem) => (
                        <>
                             <Select label={t('admin.masterdata.productCompany.company')}
                                disabled={!isManager}
                                value={selectedItem.company}
                                choices={companys.map(x => ({ value: x.code, text: x.name }))}
                                onChange={val => { if (val) setSelectedItem({ ...selectedItem, company: val }) }} />
                            <Select label={t(tBase + 'customerSegment')}
                                isClearable={true}
                                clearableLabel='None'
                                disabled={!isManager}
                                value={selectedItem.customerSegment}
                                choices={channelCustomerSegments(selectedItem.company)
                                    .map(x => ({ value: x, text: x }))}
                                onChange={customerSegment => {
                                    setSelectedItem({ ...selectedItem, customerSegment })
                                }} />
                            <Select label={t('admin.masterdata.productCompany.product')}
                                disabled={!isManager}
                                value={selectedItem.product}
                                choices={products.map(x => ({ value: x.code, text: x.code }))}
                                onChange={val => { if (val) setSelectedItem({ ...selectedItem, product: val }) }} />
                             <Select label={t('admin.masterdata.plant.site')}
                                value={selectedItem.site}
                                choices={sites.map(x => ({ value: x.code, text: x.name }))}
                                onChange={val => { if (val) setSelectedItem({ ...selectedItem, site: val }) }} />
                            <NumberField label={t(tBase + 'mondayWeight')}
                                text={selectedItem.mondayWeight}
                                precision={2}
                                onChange={value => setSelectedItem({ ...selectedItem, mondayWeight: value })} />
                            <NumberField label={t(tBase + 'tuesdayWeight')}
                                text={selectedItem.tuesdayWeight}
                                precision={2}
                                onChange={value => setSelectedItem({ ...selectedItem, tuesdayWeight: value })} />
                            <NumberField label={t(tBase + 'wednesdayWeight')}
                                text={selectedItem.wednesdayWeight}
                                precision={2}
                                onChange={value => setSelectedItem({ ...selectedItem, wednesdayWeight: value })} />
                            <NumberField label={t(tBase + 'thursdayWeight')}
                                text={selectedItem.thursdayWeight}
                                precision={2}
                                onChange={value => setSelectedItem({ ...selectedItem, thursdayWeight: value })} />
                            <NumberField label={t(tBase + 'fridayWeight')}
                                text={selectedItem.fridayWeight}
                                precision={2}
                                onChange={value => setSelectedItem({ ...selectedItem, fridayWeight: value })} />
                            <NumberField label={t(tBase + 'saturdayWeight')}
                                text={selectedItem.saturdayWeight}
                                precision={2}
                                onChange={value => setSelectedItem({ ...selectedItem, saturdayWeight: value })} />
                            <NumberField label={t(tBase + 'sundayWeight')}
                                text={selectedItem.sundayWeight}
                                precision={2}
                                onChange={value => setSelectedItem({ ...selectedItem, sundayWeight: value })} />
                        </>
                    )}
            </MasterDataShell>
        </div>
    )
}

type IsNew = { isNew: boolean }

let emptyItem = (): ChannelCustomerSegmentProduct & IsNew => ({
    id:  guid.empty,
    company: '',
    customerSegment: '',
    product: '',
    site: '',
    mondayWeight: 0,
    tuesdayWeight: 0,
    wednesdayWeight: 0,
    thursdayWeight: 0,
    fridayWeight: 0,
    saturdayWeight: 0,
    sundayWeight: 0,
    isNew: true
})

let toTableItem = (segmentProduct: ChannelCustomerSegmentProduct): TableItem<MasterDataItem<ChannelCustomerSegmentProduct>> => ({
    ...segmentProduct,
    id: segmentProduct.id,
    isModified: false
})

let styles = () =>
    createStyles({
        container: { height: '100%' }
    })

export default withStyles(styles, muiOptions)(CustomerSegmentProductMasterData)
